/* eslint-disable no-useless-escape */
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import validate from 'validate.js';
import {
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Typography,
} from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';

import {
  AuthPartnersFooterV2,
  AuthSplitLayoutV2,
  InputV2,
} from '../../../../components/v2';
import * as actionsAuth from '../../../../store/auth/actions';
import * as selectorsAuth from '../../../../store/auth/reducer';

const schema = {
  password: {
    presence: { allowEmpty: false, message: 'é obrigatório' },
  },
  confirmPassword: {
    presence: { allowEmpty: false, message: 'é obrigatório' },
    equality: 'password',
  },
};

const CRITERIA = [
  { key: 'caracters', label: 'caracteres', symbol: '8' },
  { key: 'maiuscula', label: 'MAIÚSCULA', symbol: 'A' },
  { key: 'minuscula', label: 'minúscula', symbol: 'a' },
  { key: 'numero', label: 'número', symbol: '0-9' },
  { key: 'especial', label: 'especial', symbol: '#@?&' },
];

const getPasswordStrength = password => ({
  caracters: !!(password && password.length >= 8),
  maiuscula: !!(password && /[A-Z]/.test(password)),
  minuscula: !!(password && /[a-z]/.test(password)),
  numero: !!(password && /\d/.test(password)),
  especial: !!( password && /[`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/.test(password)),
});

const isStrongPassword = password =>
  Object.values(getPasswordStrength(password)).every(Boolean);

const shouldShowConfirmMismatch = (password = '', confirmPassword = '') => (
  password.length > 0
  && confirmPassword.length >= password.length
  && confirmPassword !== password
);

const NewPasswordForm = ({
  updatePassword,
  auth,
  modeSetting,
  resolvedMode,
  onModeSettingChange,
}) => {
  const history = useHistory();
  const [errors, setErrors] = useState({});
  const [values, setValues] = useState({});
  const [viewPassword, setViewPassword] = useState(false);
  const [viewConfirmPassword, setViewConfirmPassword] = useState(false);
  const [validation, setValidation] = useState({
    caracters: false,
    maiuscula: false,
    minuscula: false,
    numero: false,
    especial: false,
  });

  const syncConfirmPasswordError = (password, confirmPassword) => {
    if (shouldShowConfirmMismatch(password, confirmPassword)) {
      setErrors(prev => ({ ...prev, confirmPassword: true }));
    } else {
      setErrors(prev => {
        const next = { ...prev };
        delete next.confirmPassword;
        return next;
      });
    }
  };

  const handleConfirmPassword = event => {
    const confirmPassword = event.target.value;
    setValues(prev => {
      syncConfirmPasswordError(prev.password || '', confirmPassword);
      return { ...prev, confirmPassword };
    });
  };

  const handlePassword = event => {
    const password = event.target.value;
    const strength = getPasswordStrength(password);
    setValidation(strength);

    if (!isStrongPassword(password)) {
      setErrors(prev => ({ ...prev, password: true }));
    } else {
      setErrors(prev => {
        const next = { ...prev };
        delete next.password;
        return next;
      });
    }

    setValues(prev => {
      const next = { ...prev, password };
      if (prev.confirmPassword) {
        syncConfirmPasswordError(password, prev.confirmPassword);
      }
      return next;
    });
  };

  const handleSave = () => {
    let errs = validate(values, schema);

    if (!isStrongPassword(values.password)) {
      errs = errs ? { ...errs, password: true } : { password: true };
    }

    setErrors(errs || {});

    if (!errs) {
      updatePassword(auth.id, values, () => {
        setErrors({});
        setValues({});
        setViewPassword(false);
        setViewConfirmPassword(false);
        setValidation({ caracters: false, maiuscula: false, minuscula: false, numero: false, especial: false });
        history.push('/v2/nsirs');
      });
    }
  };

  const criteriaColor = key => {
    if (!values.password) return 'text.disabled';
    return validation[key] ? 'success.main' : 'error.main';
  };

  return (
    <AuthSplitLayoutV2
      footer={<AuthPartnersFooterV2 />}
      modeSetting={modeSetting}
      resolvedMode={resolvedMode}
      onModeSettingChange={onModeSettingChange}
    >
      <Stack spacing={3}>
        <Stack spacing={0.5}>
          <Typography variant="h5" fontWeight={700}>
            Alterar senha
          </Typography>
          <Typography variant="body2" color="text.secondary">
            O primeiro passo é definir uma nova senha de acesso.
          </Typography>
          <Typography variant="caption" color="text.disabled">
            Necessário no primeiro acesso ou após reset de senha.
          </Typography>
        </Stack>

        <InputV2
          label="Nova Senha"
          name="password"
          isRequired
          type={viewPassword ? 'text' : 'password'}
          error={!!(errors && errors.password)}
          hideHelperText
          value={values.password || ''}
          onChange={handlePassword}
          inputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={viewPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  onClick={() => setViewPassword(v => !v)}
                  edge="end"
                  size="small"
                >
                  {viewPassword
                    ? <VisibilityOutlinedIcon fontSize="small" />
                    : <VisibilityOffOutlinedIcon fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Critérios para a senha:
          </Typography>
          <Grid container spacing={1}>
            {CRITERIA.map(c => (
              <Grid size={{ xs: c.key === 'especial' || c.key === 'numero' ? 6 : 4 }} key={c.key}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" fontWeight={700} color={criteriaColor(c.key)}>
                    {c.symbol}
                  </Typography>
                  <Typography variant="caption" color={criteriaColor(c.key)}>
                    {c.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        <InputV2
          label="Confirmar Nova Senha"
          name="confirmPassword"
          isRequired
          type={viewConfirmPassword ? 'text' : 'password'}
          error={!!errors?.confirmPassword}
          helperText={errors?.confirmPassword ? 'As senhas não são iguais' : null}
          showHelperText={!!errors?.confirmPassword}
          value={values.confirmPassword || ''}
          onChange={handleConfirmPassword}
          inputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={viewConfirmPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  onClick={() => setViewConfirmPassword(v => !v)}
                  edge="end"
                  size="small"
                >
                  {viewConfirmPassword ? (
                    <VisibilityOutlinedIcon fontSize="small" />
                  ) : (
                    <VisibilityOffOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Button
          color="primary"
          fullWidth
          size="large"
          variant="contained"
          onClick={handleSave}
        >
          Entrar
        </Button>
      </Stack>
    </AuthSplitLayoutV2>
  );
};

NewPasswordForm.propTypes = {
  updatePassword: PropTypes.func.isRequired,
  auth: PropTypes.object,
  modeSetting: PropTypes.oneOf(['system', 'light', 'dark']),
  resolvedMode: PropTypes.oneOf(['light', 'dark']),
  onModeSettingChange: PropTypes.func,
};

const mapStateToProps = state => ({
  auth: selectorsAuth.getAuth(state),
});

const mapDispatchToProps = dispatch =>
  bindActionCreators({ ...actionsAuth }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(NewPasswordForm);
